import { useState } from 'react';

import {
  tokensApi,
  type CreateApiTokenResponse,
} from '@/api/queries/tokens';
import {
  Button,
  Checkbox,
  Field,
  Icon,
  IconButton,
  Input,
  ModalRoot,
  Select,
} from '@/components/ui';

const SCOPE_OPTIONS = [
  {
    value: 'imports:linkedin',
    label: 'LinkedIn import (POST /imports/linkedin)',
  },
  {
    value: 'contacts:import',
    label: 'External contact import (POST /imports/contacts + attachments)',
  },
];

const EXPIRY_OPTIONS = [
  { value: '', label: 'Never' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
];

interface NewTokenModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called once with the newly created token + raw value so the parent can
   * surface the one-time reveal banner. The modal never holds the raw secret
   * itself after this fires.
   */
  onCreated: (response: CreateApiTokenResponse) => void;
}

export function NewTokenModal({ open, onClose, onCreated }: NewTokenModalProps) {
  const create = tokensApi.useCreate();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['imports:linkedin']);
  const [expiryDays, setExpiryDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setScopes(['imports:linkedin']);
    setExpiryDays('');
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggleScope = (scope: string, on: boolean) => {
    setScopes((prev) => (on ? [...prev, scope] : prev.filter((s) => s !== scope)));
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Token name is required');
      return;
    }
    if (scopes.length === 0) {
      setError('Pick at least one scope');
      return;
    }
    let expiresAt: string | undefined;
    if (expiryDays) {
      const days = Number(expiryDays);
      const dt = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
      expiresAt = dt.toISOString();
    }
    try {
      const res = await create.mutateAsync({ name: name.trim(), scopes, expiresAt });
      onCreated(res);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create token');
    }
  };

  return (
    <ModalRoot open={open} onClose={close} size="md" closeOnScrim>
      <div className="ds-modal-head">
        <h2>Create personal access token</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={close} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <Field
            label="Name"
            required
            hint="Something you'll recognize later, e.g. 'LinkedIn extension – work laptop'"
          >
            <Input
              autoFocus
              value={name}
              placeholder="LinkedIn extension – laptop"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Scopes" required>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SCOPE_OPTIONS.map((opt) => (
                <Checkbox
                  key={opt.value}
                  label={opt.label}
                  checked={scopes.includes(opt.value)}
                  onChange={(on) => toggleScope(opt.value, on)}
                />
              ))}
            </div>
          </Field>

          <Field label="Expires">
            <Select
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              options={EXPIRY_OPTIONS}
            />
          </Field>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'var(--red-soft)',
                color: 'var(--red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
              }}
            >
              <Icon name="alert" size={14} /> {error}
            </div>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={create.isPending}
          leftIcon={!create.isPending ? <Icon name="check" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          Create token
        </Button>
      </div>
    </ModalRoot>
  );
}
